/**
 * Free Gift Manager - Integrated with Theme
 * Automatically adds/removes a free gift product based on cart subtotal
 * Extends the theme object and reuses existing cart functionality
 */

(function() {
  'use strict';

  // Extend theme object with free gift functionality
  theme.freeGift = {
    config: {},
    processing: false,
    currentCart: null,
    giftLineItemKey: null,
    cartChangeTimeout: null,
    dedupeTimeout: null,
    pendingDiscountApplication: false,

    /**
     * Initialize free gift manager
     */
    init: function(config) {
      this.config = {
        enabled: config.enabled || false,
        mode: config.mode || 'automatic',
        productId: parseInt(config.productId) || null,
        productHandle: config.productHandle || null,
        variantId: config.variantId ? parseInt(config.variantId) : null,
        minimumAmount: parseInt(config.minimumAmount) || 0,
        progressThreshold: parseInt(config.progressThreshold) || 0,
        productTitle: config.productTitle || 'Free Gift',
        moneyFormat: config.moneyFormat || '${{amount}}',
        manualMessage: config.manualMessage || 'You qualify for a free {product_name}! Click the button to add it to your cart.',
        addButtonText: config.addButtonText || 'Add Free Gift',
        discountCode: config.discountCode || '',
        isB2BCustomer: config.isB2BCustomer || false
      };

      if (!this.config.enabled || !this.config.productId) {
        return;
      }

      // Listen for cart changes
      document.addEventListener('theme:cartchanged', this.handleCartChange.bind(this));

      // Listen for cart drawer open
      document.addEventListener('theme:open-cart-drawer', this.handleCartDrawerOpen.bind(this));
      
      // Initial check on page load
      document.addEventListener('theme:open-cart-drawer', this.checkAndUpdateGift.bind(this));

      // Only mount B2B action hooks if customer is B2B
      if (this.config.isB2BCustomer) {
        this.mountB2BActionHooks();
      }
    },

    /**
     * Handle cart change events
     */
    handleCartChange: function(e) {
      // Skip if this change was triggered by free gift system
      if (e && e.detail && (e.detail.source === 'free-gift-added' || 
                           e.detail.source === 'free-gift-removed' ||
                           e.detail.source === 'free-gift-dedupe')) {
        return;
      }

      // Debounce to avoid multiple simultaneous updates
      clearTimeout(this.cartChangeTimeout);
      this.cartChangeTimeout = setTimeout(() => {
        this.checkAndUpdateGift();
      }, 400);
    },

    /**
     * Handle cart drawer open event
     */
    handleCartDrawerOpen: function() {
      this.checkDiscountCodeOnDrawerOpen();
    },

    /**
     * Check if discount code needs to be applied when cart drawer opens
     */
    checkDiscountCodeOnDrawerOpen: async function() {
      // Skip if customer is not B2B
      if (!this.config.isB2BCustomer) {
        return;
      }

      try {
        // Check if cart drawer is actually visible
        const cartDrawer = document.querySelector('.cart-drawer-modal');
        if (!cartDrawer) {
          return;
        }

        // Fetch current cart to check if gift is present
        const cart = await this.fetchCart();
        if (!cart) {
          return;
        }

        const { hasGift } = this.analyzeCart(cart);

        // If gift is in cart, check if discount code is applied
        // Also check if we have a pending discount application
        if (hasGift || this.pendingDiscountApplication) {
          // Reset pending flag if it was set
          if (this.pendingDiscountApplication) {
            this.pendingDiscountApplication = false;
          }
          // Wait for discount code elements to be available and check if applied
          const maxAttempts = 10;
          let attempts = 0;
          
          const checkDiscountApplied = () => {
            return new Promise((resolve) => {
              const checkElements = () => {
                const clearButton = document.querySelector('#bss-b2b-clear-discount-code');
                const input = document.querySelector('#bss-b2b-discount-code-input');
                
                // If clear button exists, discount is already applied
                if (clearButton) {
                  resolve(true);
                  return;
                }
                
                // If input exists but no clear button, discount is not applied
                if (input) {
                  resolve(false);
                  return;
                }
                
                // Elements not ready yet, keep checking
                if (attempts < maxAttempts) {
                  attempts++;
                  setTimeout(checkElements, 200);
                } else {
                  // Elements not found, assume not applied and try to apply
                  resolve(false);
                }
              };
              checkElements();
            });
          };

          // Check if discount is already applied
          const isApplied = await checkDiscountApplied();
          // If discount is not applied, apply it
          if (!isApplied) {
            await this.applyDiscountCode();
          }
        }
      } catch (error) {
        console.error('Error checking discount code on drawer open:', error);
      }
    },

    /**
     * Main logic to check cart and add/remove gift
     */
    checkAndUpdateGift: async function() {
      if (this.processing) {
        return;
      }

      this.processing = true;

      try {
        // Fetch current cart state
        const cart = await this.fetchCart();
        
        if (!cart) {
          this.processing = false;
          return;
        }

        this.currentCart = cart;

        // Calculate subtotal excluding the gift item
        const { subtotal, hasGift, giftKey } = this.analyzeCart(cart);
        this.giftLineItemKey = giftKey;

        const qualifies = subtotal >= this.config.minimumAmount;

        // Handle based on mode
        if (this.config.mode === 'automatic') {
          // Automatic mode: Add or remove gift based on qualification
          if (qualifies && !hasGift) {
            await this.addGift();
          } else if (!qualifies && hasGift) {
            await this.removeGift(giftKey);
          }
        } else {
          // Manual mode: Remove gift if no longer qualifies
          if (!qualifies && hasGift) {
            await this.removeGift(giftKey);
          }
          // In manual mode, customer must click button to add gift
        }
      } catch (error) {
        console.error('Free Gift Manager error:', error);
      } finally {
        this.processing = false;
      }
    },

    /**
     * Analyze cart to find gift item and calculate subtotal
     */
    analyzeCart: function(cart) {
      let subtotal = 0;
      let hasGift = false;
      let giftKey = null;

      if (!cart || !cart.items || cart.items.length === 0) {
        return { subtotal: 0, hasGift: false, giftKey: null };
      }

      cart.items.forEach(item => {
        // Check if this is the gift item
        const isGift = item.properties && 
                       (item.properties._is_free_gift === 'true' || 
                        item.properties._is_free_gift === true);

        if (isGift && item.product_id === this.config.productId) {
          hasGift = true;
          giftKey = item.key;
        } else {
          // Only count non-gift items toward subtotal
          subtotal += item.final_line_price;
        }
      });

      return { subtotal, hasGift, giftKey };
    },

    /**
     * Add the gift product to cart
     */
    addGift: async function() {
      try {
        const variantId = this.config.variantId || await this.getFirstAvailableVariant();
        
        if (!variantId) {
          console.warn('Free Gift: No available variant found');
          return;
        }

        const formData = {
          id: variantId,
          quantity: 1,
          properties: {
            '_is_free_gift': 'true',
            '_gift_label': 'FREE GIFT'
          }
        };

        const response = await fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Failed to add gift:', errorData.description || errorData.message);
          return;
        }

        // Set flag to apply discount code after cart is fully updated
        this.pendingDiscountApplication = true;

        // Trigger cart refresh with source identifier
        document.dispatchEvent(
          new CustomEvent('theme:cartchanged', { 
            bubbles: true, 
            cancelable: false,
            detail: { 
              source: 'free-gift-added',
            }
          })
        );

      } catch (error) {
        console.error('Error adding free gift:', error);
      }
    },

    /**
     * Remove the gift product from cart
     */
    removeGift: async function(giftKey) {
      if (!giftKey) {
        return;
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: giftKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          console.error('Failed to remove gift');
          return;
        }

        // Clear discount code when gift is removed
        await this.clearDiscountCode();

        // Trigger cart refresh with source identifier
        document.dispatchEvent(
          new CustomEvent('theme:cartchanged', { 
            bubbles: true, 
            cancelable: false,
            detail: { source: 'free-gift-removed' }
          })
        );

      } catch (error) {
        console.error('Error removing free gift:', error);
      }
    },

    /**
     * Fetch current cart state
     */
    fetchCart: async function() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        return await response.json();
      } catch (error) {
        console.error('Error fetching cart:', error);
        return null;
      }
    },

    /**
     * Get the first available variant of the gift product
     */
    getFirstAvailableVariant: async function() {
      if (!this.config.productHandle) {
        return null;
      }

      try {
        const response = await fetch(`/products/${this.config.productHandle}.js`);
        if (!response.ok) {
          throw new Error('Failed to fetch product');
        }
        const product = await response.json();
        
        // Find first available variant
        const availableVariant = product.variants.find(v => v.available);
        return availableVariant ? availableVariant.id : null;
      } catch (error) {
        console.error('Error fetching product variants:', error);
        return null;
      }
    },

    /**
     * Check if customer currently qualifies for the gift
     */
    customerQualifies: function() {
      if (!this.currentCart) {
        return false;
      }

      const { subtotal } = this.analyzeCart(this.currentCart);
      return subtotal >= this.config.minimumAmount;
    },

    /**
     * Get amount remaining to qualify
     */
    getAmountRemaining: function() {
      if (!this.currentCart) {
        return this.config.minimumAmount;
      }

      const { subtotal } = this.analyzeCart(this.currentCart);
      const remaining = this.config.minimumAmount - subtotal;
      return Math.max(0, remaining);
    },

    /**
     * Check if customer is close to qualifying (within progress threshold)
     */
    isCloseToQualifying: function() {
      const remaining = this.getAmountRemaining();
      return remaining > 0 && remaining <= this.config.progressThreshold;
    },

    /**
     * Format money amount using theme's formatter
     */
    formatMoney: function(cents) {
      if (theme.Shopify && theme.Shopify.formatMoney) {
        return theme.Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      
      // Fallback formatting
      const dollars = (cents / 100).toFixed(2);
      return this.config.moneyFormat.replace('{{amount}}', dollars).replace('{{ amount }}', dollars);
    },

    /**
     * Check if discount code application failed
     */
    checkDiscountCodeError: function() {
      const messageDiv = document.querySelector('#bss-b2b-discount-code-message');
      if (!messageDiv) {
        return false;
      }
      
      const errorText = messageDiv.textContent || messageDiv.innerText || '';
      return errorText.includes('Failed to apply this discount code');
    },

    /**
     * Apply the discount code programmatically with retry logic
     */
    applyDiscountCode: async function(retryAttempt = 0) {
      // Skip if customer is not B2B
      if (!this.config.isB2BCustomer) {
        return;
      }

      // Skip if discount code is not configured
      if (!this.config.discountCode || this.config.discountCode.trim() === '') {
        return;
      }

      const maxRetries = 3;
      const retryDelay = 800; // Wait 800ms between retries

      try {
        // Wait for discount code elements to be available
        const maxAttempts = 10;
        let attempts = 0;
        
        const waitForElements = () => {
          return new Promise((resolve) => {
            const checkElements = () => {
              const input = document.querySelector('#bss-b2b-discount-code-input');
              const submitButton = document.querySelector('#bss-b2b-discount-code-submit');
              
              if (input && submitButton) {
                resolve({ input, submitButton });
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkElements, 200);
              } else {
                console.warn('Free Gift: Discount code elements not found');
                resolve(null);
              }
            };
            checkElements();
          });
        };

        const elements = await waitForElements();
        if (!elements) {
          return;
        }

        const { input, submitButton } = elements;

        // Check if discount code is already applied
        const clearButton = document.querySelector('#bss-b2b-clear-discount-code');
        if (clearButton) {
          // Already applied, skip
          return;
        }

        // Fill in the discount code
        input.value = this.config.discountCode;
        
        // Trigger input event to ensure the value is recognized
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Wait a moment for any validation
        await new Promise(resolve => setTimeout(resolve, 100));

        // Click the submit button
        if (!submitButton.disabled) {
          submitButton.click();
          
          // Wait for the response (BSS B2B processes asynchronously)
          await new Promise(resolve => setTimeout(resolve, 600));
          
          // Check if application failed
          if (this.checkDiscountCodeError()) {
            console.warn(`Free Gift: Discount code application failed (attempt ${retryAttempt + 1}/${maxRetries})`);
            
            // Retry if we haven't exceeded max retries
            if (retryAttempt < maxRetries - 1) {
              console.log(`Free Gift: Retrying discount code application in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              return this.applyDiscountCode(retryAttempt + 1);
            } else {
              console.error('Free Gift: Discount code application failed after all retry attempts');
            }
          } else {
            // Success - check if discount was actually applied
            const successClearButton = document.querySelector('#bss-b2b-clear-discount-code');
            if (successClearButton) {
              console.log('Free Gift: Discount code applied successfully');
            }
          }
        }

      } catch (error) {
        console.error('Error applying discount code:', error);
        
        // Retry on error if we haven't exceeded max retries
        if (retryAttempt < maxRetries - 1) {
          console.log(`Free Gift: Retrying discount code application after error in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return this.applyDiscountCode(retryAttempt + 1);
        }
      }
    },

     /**
      * Mount all B2B action hooks
      * Registers all custom action hooks in a single event handler
      */
     mountB2BActionHooks: function() {
       const registerAllActions = () => {
         if (!window.BSS_B2B?.custom?.addAction) {
           return false;
         }

         // Register deduplication hook - runs before price calculations
         // This action is called at the START of execCartPriceFlow, right before:
         // 1. Reading cartStorage.getAll()
         // 2. Calculating totals
         // 3. Showing subtotal
         window.BSS_B2B.custom.addAction('custom:cart/delayCart', async (payload) => {
           console.log('Free Gift: custom:cart/delayCart action called - deduplicating before price calculation');
           this.deduplicateCartStorage();
         });

         // Register discount code application hook - runs after cart updates and price calculations
         // This ensures the cart drawer is rendered and discount code elements are available
         window.BSS_B2B.custom.addAction('custom:cart/showSubtotalPrice', async (payload) => {
           if (this.pendingDiscountApplication) {
             console.log('Free Gift: custom:cart/showSubtotalPrice hook - applying discount code');
             this.pendingDiscountApplication = false;
             
             // Wait a bit for discount code elements to be rendered
             setTimeout(async () => {
               await this.applyDiscountCode();
             }, 300);
           }
         });

         return true;
       };

       // Register all actions when BSS_B2B observer is loaded
       const handleObserverLoaded = () => {
         if (!registerAllActions()) {
           console.warn('Free Gift: BSS_B2B.custom.addAction not available');
         }
       };

       // Check if event already fired
       if (window.BSS_B2B?.observer) {
         handleObserverLoaded();
       } else {
         // Wait for the observer loaded event
         window.addEventListener('bss_b2b:loadedObserver', handleObserverLoaded, { once: true });
       }
     },

    /**
     * Handle CartUpdate event - deduplicates cartStorage after a delay
     * This ensures cartStorage.load() has completed
     * Uses debouncing to avoid running too frequently
     */
    handleCartUpdate: function(event) {
      console.log('Free Gift: handleCartUpdate called', event);
      
      // Clear any pending deduplication
      clearTimeout(this.dedupeTimeout);
      
      // Debounce: wait for cartStorage to be fully loaded
      // The middleware processes CartUpdate and loads cartStorage asynchronously
      this.dedupeTimeout = setTimeout(() => {
        // Verify cartStorage is ready before deduplicating
        if (window.BSS_B2B?.storage?.cartStorage) {
          console.log('Free Gift: Deduplicating cartStorage');
          this.deduplicateCartStorage(event);
        } else {
          console.warn('Free Gift: cartStorage not available for deduplication');
        }
      }, 600); // 600ms delay to ensure cartStorage.load() has completed
    },

    /**
     * Deduplicate cartStorage - removes duplicate variant IDs with same properties
     * This runs before price calculations in execCartPriceFlow
     * Only removes items from cartStorage that are NOT in the actual Shopify cart
     * This ensures we keep the items that actually exist in the cart and remove phantom duplicates
     */
    deduplicateCartStorage: function() {
      // Skip if customer is not B2B
      if (!this.config.isB2BCustomer) {
        return;
      }

      if (!window.BSS_B2B?.storage?.cartStorage) {
        return;
      }

      try {
        const allItems = window.BSS_B2B.storage.cartStorage.getAllItem();
        if (!allItems?.length) {
          return;
        }

        // Get the actual cart items from Shopify (client/server cart)
        const actualCartItems = window.BSS_B2B?.shopData?.cart?.items || [];
        const actualCartKeys = new Set(actualCartItems.map(item => item.key));

        // Group items by variant_id + normalized properties
        const variantGroups = new Map();
        
        allItems.forEach(item => {
          if (!item.variant_id) return;
          
          // Create unique key: variant_id + normalized properties
          const groupKey = `${item.variant_id}_${this.normalizeProperties(item.properties)}`;
          
          if (!variantGroups.has(groupKey)) {
            variantGroups.set(groupKey, []);
          }
          variantGroups.get(groupKey).push(item);
        });

        // Find and remove duplicates - only remove phantom items (not in actual cart)
        const itemsToRemove = [];
        
        variantGroups.forEach(items => {
          if (items.length > 1) {
            // Separate items into those that exist in cart vs those that don't
            const itemsInCart = items.filter(item => actualCartKeys.has(item.key));
            const itemsNotInCart = items.filter(item => !actualCartKeys.has(item.key));

            // Only remove phantom items (items not in actual cart)
            // Keep ALL items that exist in the actual cart, even if there are duplicates
            if (itemsNotInCart.length > 0) {
              itemsToRemove.push(...itemsNotInCart);
              
              console.log('Free Gift: Found duplicates for variant', items[0].variant_id, {
                keeping: itemsInCart.map(i => i.key),
                removing: itemsNotInCart.map(i => i.key),
                inCart: itemsInCart.length,
                notInCart: itemsNotInCart.length
              });
            }
            // If all items exist in cart, don't remove any (they're legitimate duplicates)
          }
        });

        // Remove duplicates from cartStorage only
        if (itemsToRemove.length > 0) {
          console.warn('Free Gift: Removing duplicate items from cartStorage:', itemsToRemove.length);
          
          itemsToRemove.forEach(item => {
            console.log('Free Gift: Removing duplicate from cartStorage:', {
              key: item.key,
              variant_id: item.variant_id,
              product_title: item.product_title,
              existsInCart: actualCartKeys.has(item.key)
            });
            
            // Remove from cartStorage only (for correct B2B calculations)
            window.BSS_B2B.storage.cartStorage.delete(item.key);
          });
        }
      } catch (error) {
        console.error('Free Gift: Error deduplicating cartStorage:', error);
      }
    },

    /**
     * Normalize properties for comparison
     */
    normalizeProperties: function(properties) {
      if (!properties || typeof properties !== 'object') {
        return '';
      }
      // Convert to sorted key-value string for consistent comparison
      return JSON.stringify(
        Object.entries(properties)
          .map(([k, v]) => [k, String(v)])
          .sort(([a], [b]) => a.localeCompare(b))
      );
    },


    /**
     * Clear the discount code programmatically
     */
    clearDiscountCode: async function() {
      try {
        // Wait for clear button to be available
        const maxAttempts = 10;
        let attempts = 0;
        
        const waitForClearButton = () => {
          return new Promise((resolve) => {
            const checkButton = () => {
              const clearButton = document.querySelector('#bss-b2b-clear-discount-code');
              
              if (clearButton) {
                resolve(clearButton);
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkButton, 200);
              } else {
                // Clear button not found, might not be applied
                resolve(null);
              }
            };
            checkButton();
          });
        };

        const clearButton = await waitForClearButton();
        if (clearButton) {
          clearButton.click();
        }

      } catch (error) {
        console.error('Error clearing discount code:', error);
      }
    }
  };

})();

/**
 * Handle manual "Add Free Gift" button clicks
 * The offer display is handled by Liquid (server-side rendering)
 */
(function() {
  'use strict';

  // Use event delegation for dynamically rendered buttons
  document.addEventListener('click', async function(e) {
    const button = e.target.closest('.js-add-free-gift');
    if (!button || button.disabled) {
      return;
    }

    e.preventDefault();
    button.disabled = true;
    button.classList.add('button--loading');

    try {
      // Add the gift to cart (this will also apply the discount code)
      await theme.freeGift.addGift();
      
      // Cart will refresh automatically via section rendering
      // Button will disappear because Liquid will hide the offer
      
    } catch (error) {
      console.error('Error adding free gift:', error);
      button.disabled = false;
      button.classList.remove('button--loading');
    }
  });

})();


// Initialize when theme and config are ready
(function initFreeGift() {
  if (typeof theme === 'undefined') {
    // Theme not loaded yet, wait
    setTimeout(initFreeGift, 100);
    return;
  }

  if (typeof window.freeGiftConfig !== 'undefined' && theme.freeGift) {
    theme.freeGift.init(window.freeGiftConfig);
  }
})();

